/**
 * Data API client for Vidi.
 * Uses Supabase client directly with RLS for auth.
 * All operations are scoped to the authenticated user.
 */

import { supabase } from '@/lib/supabase';
import { captureProductEvent } from './posthogAnalytics';
import { getDefaultLanguagePair } from '@/config/languages';
import { getCachedUserId } from './userIdCache';
import { isDurableCardImageUrl, MirrorFailedError, mirrorImage, shouldMirror } from './mirrorImage';

async function getUserId(expectedUserId?: string): Promise<string> {
  const userId = await getCachedUserId();
  if (expectedUserId !== undefined && expectedUserId !== userId) {
    throw new Error('Your account changed. Please try again.');
  }
  return userId;
}

export async function fetchCards(deckId?: string): Promise<unknown[]> {
  const userId = await getUserId();
  let query = supabase
    .from('cards')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false });
  if (deckId) {
    query = query.eq('deck_id', deckId);
  }
  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function createCard(
  card: Record<string, unknown>,
  expectedUserId?: string,
): Promise<unknown> {
  const userId = await getUserId(expectedUserId);
  // Persistence invariant (owner 2026-09-05): the card stores OUR copy of the
  // photo, never a rented Pexels/Unsplash/Pixabay/Google URL. This choke
  // point covers Create, batch/quick-save, WordPack, Library replace,
  // CardEditSheet, review-time swap, /try signup migration, and offline
  // replays. Empty imageUrl is a deliberate draft (Skip) and is allowed.
  // If the copy fails, we throw — a save that still points at a third-party
  // host is the old bug. Choice is the learning; keeping the file is what
  // makes that choice durable.
  const requested = typeof card.imageUrl === 'string' ? card.imageUrl : '';
  const stableImageUrl = shouldMirror(requested) ? await mirrorImage(requested) : requested;
  if (!isDurableCardImageUrl(stableImageUrl)) {
    throw new MirrorFailedError(requested);
  }
  // Mirroring is async: the account may have switched while it ran, and the row
  // below would then be written for the wrong learner.
  await getUserId(userId);
  const importKey = typeof card.importKey === 'string' && card.importKey ? card.importKey : null;
  const row = {
    user_id: userId,
    import_key: importKey,
    deck_id: card.deckId,
    card_origin:
      card.cardOrigin === 'learner' || card.cardOrigin === 'starter' ? card.cardOrigin : null,
    target_word: card.targetWord,
    native_word: card.nativeWord,
    target_example: card.targetExample ?? '',
    native_example: card.nativeExample ?? '',
    word_gender: card.wordGender ?? null,
    word_plural: card.wordPlural ?? null,
    word_class: card.wordClass ?? 'noun',
    is_irregular: card.isIrregular ?? false,
    pronunciation_ipa: card.pronunciationIpa ?? '',
    image_url: stableImageUrl,
    // One stable asset: a separate third-party thumbnail would rot on its
    // own clock. Drafts stay empty; everything else is our storage URL.
    image_thumbnail: stableImageUrl,
    image_source: card.imageSource ?? 'Pexels',
    image_provenance: card.imageProvenance ?? null,
    card_direction: card.cardDirection ?? 'native_to_target',
    word_type: card.wordType ?? 'concrete',
    srs_state: card.srsState ?? null,
    due_date: card.dueDate ?? new Date().toISOString(),
    last_reviewed: card.lastReviewed ?? null,
    is_favorite: card.isFavorite ?? false,
    status: card.status ?? 'active',
    language_pair: card.languagePair ?? getDefaultLanguagePair().code,
    card_type: card.cardType ?? 'word',
    formal_variant: card.formalVariant ?? null,
    informal_variant: card.informalVariant ?? null,
    usage_context: card.usageContext ?? null,
    example_dialogue_formal: card.exampleDialogueFormal ?? null,
    example_dialogue_informal: card.exampleDialogueInformal ?? null,
    response_formal: card.responseFormal ?? null,
    response_informal: card.responseInformal ?? null,
    synonyms: card.synonyms ?? null,
    word_forms: card.wordForms ?? null,
    personal_note: card.personalNote ?? null,
    parent_card_id: card.parentCardId ?? null,
    grammar_prompt: card.grammarPrompt ?? null,
  };

  const { data, error } = await supabase.from('cards').insert(row).select().single();
  if (error && importKey && error.code === '23505') {
    // The same Try card was already imported (other browser, other tab, or a late write
    // after a lease lapsed). Hand back that durable row untouched: later edits stay, and
    // no second row or second analytics event is made. Only this user's own key matches.
    const { data: existing, error: lookupError } = await supabase
      .from('cards')
      .select()
      .eq('user_id', userId)
      .eq('import_key', importKey)
      .single();
    if (lookupError || !existing) throw new Error(lookupError?.message ?? 'Card import conflict');
    // The marker lets an import report already_saved instead of claiming a new write.
    return { ...existing, alreadyImported: true };
  }
  if (error) throw new Error(error.message);
  // Capture only after PostgREST returns the persisted row, including drafts.
  // The card UUID is a stable event UUID; PostHog's server dedup is eventual.
  if (typeof data?.id === 'string' && data.card_origin === 'learner') {
    const source = String(card.imageSource || '').toLowerCase();
    const sourceCategory = source.includes('ai')
      ? 'ai'
      : source.includes('serp')
        ? 'search'
        : source.includes('pexels') || source.includes('pixabay')
          ? 'stock'
          : source
            ? 'other'
            : 'none';
    const language =
      typeof card.languagePair === 'string' && /^[a-z]{2}-[a-z]{2}$/i.test(card.languagePair)
        ? card.languagePair.toLowerCase()
        : 'other';
    captureProductEvent(
      'card_saved',
      {
        card_id: data.id,
        has_image: stableImageUrl !== '',
        source_category: sourceCategory,
        language_pair: language,
      },
      data.id,
      userId,
    );
  }
  // card_count maintained by DB trigger (trg_deck_card_count)
  return data;
}

export async function updateCard(id: string, updates: Record<string, unknown>): Promise<unknown> {
  // card_count maintained by DB trigger (trg_deck_card_count) on deck_id change
  const userId = await getUserId();
  const snake: Record<string, unknown> = {};
  const map: Record<string, string> = {
    deckId: 'deck_id',
    targetWord: 'target_word',
    nativeWord: 'native_word',
    targetExample: 'target_example',
    nativeExample: 'native_example',
    wordGender: 'word_gender',
    wordPlural: 'word_plural',
    wordClass: 'word_class',
    isIrregular: 'is_irregular',
    pronunciationIpa: 'pronunciation_ipa',
    imageUrl: 'image_url',
    imageThumbnail: 'image_thumbnail',
    imageSource: 'image_source',
    imageProvenance: 'image_provenance',
    imageBrokenAt: 'image_broken_at',
    srsState: 'srs_state',
    dueDate: 'due_date',
    lastReviewed: 'last_reviewed',
    isFavorite: 'is_favorite',
    status: 'status',
    cardType: 'card_type',
    cardDirection: 'card_direction',
    wordType: 'word_type',
    synonyms: 'synonyms',
    wordForms: 'word_forms',
    formalVariant: 'formal_variant',
    informalVariant: 'informal_variant',
    usageContext: 'usage_context',
    exampleDialogueFormal: 'example_dialogue_formal',
    exampleDialogueInformal: 'example_dialogue_informal',
    responseFormal: 'response_formal',
    responseInformal: 'response_informal',
    personalNote: 'personal_note',
    parentCardId: 'parent_card_id',
    grammarPrompt: 'grammar_prompt',
  };
  for (const [k, v] of Object.entries(updates)) {
    if (v !== undefined) {
      const col = map[k] ?? k;
      snake[col] = v;
    }
  }

  // Same persistence invariant as createCard: copy any external image into
  // our storage before it lands on the row (Library replace, CardEditSheet,
  // review-time swap). A successful copy also clears the broken flag so the
  // card returns to review. If the copy fails we throw and leave the row
  // untouched — never clear the flag in exchange for a rented URL.
  if ('image_url' in snake && !('image_provenance' in snake)) snake.image_provenance = null;
  if (typeof snake.image_url === 'string' && snake.image_url) {
    if (shouldMirror(snake.image_url)) {
      const mirrored = await mirrorImage(snake.image_url);
      snake.image_url = mirrored;
      snake.image_thumbnail = mirrored;
    }
    if (!isDurableCardImageUrl(String(snake.image_url))) {
      throw new MirrorFailedError(String(snake.image_url));
    }
    if (!('image_broken_at' in snake)) {
      snake.image_broken_at = null;
    }
  }

  const { data, error } = await supabase
    .from('cards')
    .update(snake)
    .eq('id', id)
    .eq('user_id', userId)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteCard(id: string): Promise<void> {
  // card_count maintained by DB trigger (trg_deck_card_count)
  const userId = await getUserId();
  const { error } = await supabase.from('cards').delete().eq('id', id).eq('user_id', userId);
  if (error) throw new Error(error.message);
}

export async function fetchDecks(): Promise<unknown[]> {
  const userId = await getUserId();
  const { data, error } = await supabase
    .from('decks')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function createDeck(
  deck: Record<string, unknown>,
  expectedUserId?: string,
): Promise<unknown> {
  const userId = await getUserId(expectedUserId);
  const row = {
    user_id: userId,
    name: deck.name,
    emoji: deck.emoji ?? '📚',
    language_pair: deck.languagePair ?? getDefaultLanguagePair().code,
    is_default: deck.isDefault ?? false,
  };
  const { data, error } = await supabase.from('decks').insert(row).select().single();
  if (error) throw new Error(error.message);
  return data;
}

export async function updateDeck(id: string, updates: Record<string, unknown>): Promise<unknown> {
  const userId = await getUserId();
  const snake: Record<string, unknown> = {};
  const map: Record<string, string> = {
    name: 'name',
    emoji: 'emoji',
    languagePair: 'language_pair',
    cardCount: 'card_count',
    isDefault: 'is_default',
  };
  for (const [k, v] of Object.entries(updates)) {
    if (v !== undefined) {
      const col = map[k] ?? k;
      snake[col] = v;
    }
  }
  const { data, error } = await supabase
    .from('decks')
    .update(snake)
    .eq('id', id)
    .eq('user_id', userId)
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function deleteDeck(id: string): Promise<void> {
  const userId = await getUserId();
  const { error } = await supabase.from('decks').delete().eq('id', id).eq('user_id', userId);
  if (error) throw new Error(error.message);
}

export async function fetchUserSettings(): Promise<unknown> {
  const { data, error } = await supabase.from('user_settings').select('*').single();
  if (error) {
    if (error.code === 'PGRST116') {
      // No row - upsert defaults
      const userId = await getUserId();
      await supabase.from('user_settings').upsert({ user_id: userId }, { onConflict: 'user_id' });
      const { data: newRow } = await supabase.from('user_settings').select('*').single();
      return newRow ?? {};
    }
    throw new Error(error.message);
  }
  return data ?? {};
}

export async function updateUserSettings(
  updates: Record<string, unknown>,
  expectedUserId?: string,
): Promise<unknown> {
  const snake: Record<string, unknown> = {};
  // Client-owned settings are intentionally allowlisted. Billing/admin fields
  // such as is_pro and Stripe columns are service-role owned; accepting raw
  // fallback keys here would let a browser write privileged state directly.
  const map: Record<string, string> = {
    theme: 'theme',
    dailyNewCardLimit: 'daily_new_card_limit',
    ttsEnabled: 'tts_enabled',
    ttsSpeed: 'tts_speed',
    autoPlayTts: 'auto_play_tts',
    currentLanguagePair: 'current_language_pair',
    reviewMode: 'review_mode',
    onboardingCompleted: 'onboarding_completed',
    uiLocale: 'ui_locale',
    immersionMode: 'immersion_mode',
    cefrLevel: 'cefr_level',
    streak: 'streak',
    lastReviewDate: 'last_review_date',
    milestonesSeen: 'milestones_seen',
    streakFreezeAvailable: 'streak_freeze_available',
    username: 'username',
    soundEffectsEnabled: 'sound_effects_enabled',
    xp: 'xp',
    level: 'level',
    totalXpEarned: 'total_xp_earned',
    dailyXp: 'daily_xp',
    dailyXpDate: 'daily_xp_date',
    bestCombo: 'best_combo',
    challengeStreak: 'challenge_streak',
    lastChallengeDate: 'last_challenge_date',
    streakBrokenAt: 'streak_broken_at',
    streakRecoveryUsed: 'streak_recovery_used',
    hapticEnabled: 'haptic_enabled',
    autoEasyThresholdMs: 'auto_easy_threshold_ms',
    cardCreationPreset: 'card_creation_preset',
    cardCreationModules: 'card_creation_modules',
    imagePickerMode: 'image_picker_mode',
    swipeSensitivity: 'swipe_sensitivity',
  };
  for (const [k, v] of Object.entries(updates)) {
    if (v !== undefined) {
      const col = map[k];
      if (!col) {
        throw new Error(`Unsupported user_settings update field: ${k}`);
      }
      snake[col] = v;
    }
  }

  const userId = await getUserId(expectedUserId);

  const { data, error } = await supabase
    .from('user_settings')
    .upsert({ user_id: userId, ...snake }, { onConflict: 'user_id' })
    .select()
    .single();
  if (error) throw new Error(error.message);
  return data;
}

export async function fetchReviewHistory(days?: number): Promise<unknown[]> {
  const userId = await getUserId();
  let query = supabase
    .from('review_history')
    .select('*')
    .eq('user_id', userId)
    .order('reviewed_at', { ascending: false });

  if (days) {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - days);
    query = query.gte('reviewed_at', cutoff.toISOString());
  }

  const { data, error } = await query;
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function addReviewHistory(entry: {
  cardId: string;
  rating: number;
  reviewDurationMs: number;
  reviewedAt?: string;
}): Promise<unknown> {
  const userId = await getUserId();
  const row: Record<string, unknown> = {
    user_id: userId,
    card_id: entry.cardId,
    rating: entry.rating,
    review_duration_ms: entry.reviewDurationMs ?? 0,
  };
  // Preserve original timestamp for offline-synced reviews
  if (entry.reviewedAt) {
    row.reviewed_at = entry.reviewedAt;
  }
  const { data, error } = await supabase.from('review_history').insert(row).select().single();
  if (error) throw new Error(error.message);
  return data;
}

/** Delete the most recent review history entry for a card (used by undo). */
export async function deleteLastReviewForCard(cardId: string): Promise<void> {
  const userId = await getUserId();
  // Find the most recent entry for this card
  const { data: rows, error: fetchErr } = await supabase
    .from('review_history')
    .select('id')
    .eq('user_id', userId)
    .eq('card_id', cardId)
    .order('reviewed_at', { ascending: false })
    .limit(1);
  if (fetchErr) throw new Error(fetchErr.message);
  if (rows && rows.length > 0) {
    const { error: delErr } = await supabase.from('review_history').delete().eq('id', rows[0].id);
    if (delErr) throw new Error(delErr.message);
  }
}

/** Delete all user data atomically via Supabase RPC (single transaction). */
export async function deleteAllUserData(): Promise<void> {
  const { error } = await supabase.rpc('delete_all_user_data');
  if (error) throw new Error(`Failed to delete all data: ${error.message}`);
}

// ─── Grammar Progress ────────────────────────────────────────

export async function fetchGrammarProgress(): Promise<unknown[]> {
  const userId = await getUserId();
  const { data, error } = await supabase.from('grammar_progress').select('*').eq('user_id', userId);
  if (error) throw new Error(error.message);
  return data ?? [];
}

export async function upsertGrammarProgress(
  guideId: string,
  sectionIndex: number,
  correctDelta: number,
  totalDelta: number,
): Promise<void> {
  const userId = await getUserId();
  const { error } = await supabase.rpc('upsert_grammar_progress', {
    p_user_id: userId,
    p_guide_id: guideId,
    p_section_index: sectionIndex,
    p_correct_delta: correctDelta,
    p_total_delta: totalDelta,
  });
  if (error) throw new Error(error.message);
}
